import { useState, useEffect } from 'react';
import { api } from '../../../api';
import type { OnboardingStatus, PrimaryGoal, ReachProfile } from '../../../types';
import { CompetitorList } from '../../../components/CompetitorList';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  domainId: string;
  domain: string;
  tier: string;
  status: OnboardingStatus;
  onStatusUpdate: (next: OnboardingStatus) => void;
  onContinue: () => void;
  onSkip: () => void;
}

interface GoalOption {
  value: PrimaryGoal;
  label: string;
  description: string;
  helper: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const GOAL_OPTIONS: GoalOption[] = [
  {
    value:       'mention_rate',
    label:       'AI Visibility',
    description: 'I want to be the answer when someone asks AI for what I do.',
    helper:      "We'll prioritise GEO citation tracking, FAQ generation, and AEO schema across your reporting.",
  },
  {
    value:       'organic_rank',
    label:       'Organic Search',
    description: 'I want to rank higher in traditional search.',
    helper:      "We'll prioritise keyword tracking, content briefs, and backlink monitoring.",
  },
  {
    value:       'local_visibility',
    label:       'Local Visibility',
    description: 'I want to be found by people near my business.',
    helper:      "We'll prioritise local SERP scans, GBP optimisation, and local FAQ schema.",
  },
];

const TIER_TRACKED_LIMITS: Record<string, number> = {
  free:    0,
  starter: 5,
  growth:  15,
  scale:   50,
};

// ── Advisory logic ────────────────────────────────────────────────────────────

function getAdvisory(reach: ReachProfile | null, goal: PrimaryGoal | null): string | null {
  if (!reach || !goal) return null;
  if (reach === 'local' && goal === 'organic_rank') {
    return "Heads up — local reach domains tend to benefit more from local visibility or mention rate goals. You can change this anytime in settings.";
  }
  if (reach === 'global' && goal === 'local_visibility') {
    return "Heads up — global reach domains tend to focus on mention rate or organic rank goals. Local visibility is better suited for city-level targeting. You can change this anytime in settings.";
  }
  return null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function GoalsCompetitorsStep({ domainId, tier, status, onStatusUpdate, onContinue, onSkip }: Props) {
  // ── Goals state ───────────────────────────────────────────────────────────
  const [selectedGoal, setSelectedGoal] = useState<PrimaryGoal | null>(status.primaryGoal);
  const [goalSaving, setGoalSaving] = useState(false);
  const [goalSaved, setGoalSaved] = useState(status.progress.goal_chosen === true);
  const [goalError, setGoalError] = useState('');

  // ── Competitors state ─────────────────────────────────────────────────────
  const [competitorChosen, setCompetitorChosen] = useState(status.progress.competitor_chosen === true);

  // ── Reach for cross-card advisory ─────────────────────────────────────────
  const [reachProfile, setReachProfile] = useState<ReachProfile | null>(null);

  useEffect(() => {
    api.domains.get(domainId)
      .then((d) => setReachProfile(d.reachProfile))
      .catch(() => null);
  }, [domainId]);

  // ── Goal save ─────────────────────────────────────────────────────────────
  async function handleGoalSave() {
    if (!selectedGoal) return;
    setGoalSaving(true);
    setGoalError('');
    try {
      await api.onboarding.setPrimaryGoal(domainId, selectedGoal);
      const nextStatus = await api.onboarding.getStatus(domainId);
      onStatusUpdate(nextStatus);
      setGoalSaved(true);
    } catch {
      setGoalError('Failed to save goal. Please try again.');
    } finally {
      setGoalSaving(false);
    }
  }

  // ── Competitor added callback ─────────────────────────────────────────────
  async function handleCompetitorAdded() {
    if (competitorChosen) return; // already flipped — no redundant API call
    try {
      await api.onboarding.patchProgress(domainId, { competitor_chosen: true });
      const nextStatus = await api.onboarding.getStatus(domainId);
      onStatusUpdate(nextStatus);
      setCompetitorChosen(true);
    } catch {
      // non-fatal — user can still continue
    }
  }

  // ── Advisory + continue ───────────────────────────────────────────────────
  const advisory = getAdvisory(reachProfile, selectedGoal);
  const canContinue =
    status.progress.goal_chosen === true ||
    status.progress.competitor_chosen === true ||
    goalSaved ||
    competitorChosen;

  const trackedLimit = TIER_TRACKED_LIMITS[tier] ?? 5;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="goals-competitors-step">
      <div>
        <h2 className="text-xl font-semibold text-brand-ink">Goals & Competitors</h2>
        <p className="text-sm text-brand-muted mt-1">
          Choose your primary optimisation goal and tell us who you're competing against.
        </p>
      </div>

      {/* ── Goals card ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4" data-testid="goals-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-brand-ink text-sm">Primary goal</h3>
            <p className="text-xs text-brand-muted mt-0.5">
              This shapes what fixmyseo focuses on when generating insights for you.
            </p>
          </div>
          {status.progress.goal_chosen && (
            <span className="text-xs text-brand-green font-medium bg-green-50 px-2 py-0.5 rounded-full">Saved</span>
          )}
        </div>

        <div className="space-y-2" data-testid="goal-options">
          {GOAL_OPTIONS.map((option) => {
            const isSelected = selectedGoal === option.value;
            return (
              <button
                key={option.value}
                type="button"
                data-testid={`goal-option-${option.value}`}
                onClick={() => setSelectedGoal(option.value)}
                aria-pressed={isSelected}
                className={`w-full text-left rounded-xl border-2 px-4 py-3.5 transition-colors ${
                  isSelected
                    ? 'border-brand-green bg-green-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`font-semibold text-sm ${isSelected ? 'text-brand-green' : 'text-brand-ink'}`}>
                    {option.label}
                  </span>
                  {isSelected && (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-0.5">{option.description}</p>
                {isSelected && (
                  <p className="text-xs text-brand-green mt-1.5 italic" data-testid={`goal-helper-${option.value}`}>{option.helper}</p>
                )}
              </button>
            );
          })}
        </div>

        {/* Cross-card advisory */}
        {advisory && (
          <p
            className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
            data-testid="goal-reach-advisory"
          >
            {advisory}
          </p>
        )}

        {goalError && <p className="text-xs text-red-500">{goalError}</p>}

        <div className="flex items-center gap-4">
          <button
            type="button"
            data-testid="save-goal-btn"
            disabled={!selectedGoal || goalSaving}
            onClick={() => void handleGoalSave()}
            className="px-4 py-2 bg-brand-green text-white rounded-lg text-sm font-medium hover:bg-brand-green/90 disabled:opacity-50"
          >
            {goalSaving ? 'Saving…' : 'Save goal'}
          </button>
          <button
            type="button"
            data-testid="skip-goal-link"
            onClick={onContinue}
            className="text-sm text-brand-muted hover:text-brand-ink"
          >
            I'll choose later
          </button>
        </div>
      </div>

      {/* ── Competitors card ────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4" data-testid="competitors-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-brand-ink text-sm">Competitors</h3>
            <p className="text-xs text-brand-muted mt-0.5">
              Accept suggested competitors or add your own. This powers competitor-gap reporting.
            </p>
          </div>
          {(status.progress.competitor_chosen || competitorChosen) && (
            <span className="text-xs text-brand-green font-medium bg-green-50 px-2 py-0.5 rounded-full">Saved</span>
          )}
        </div>

        <CompetitorList
          ownedDomainId={domainId}
          availableSlots={trackedLimit}
          onCompetitorAdded={() => void handleCompetitorAdded()}
          emptyStateMessage="We haven't suggested competitors yet. Once your verification and business profile are complete, we'll generate a list automatically. You can also add some manually here."
        />
      </div>

      {/* ── Footer navigation ───────────────────────────────────────────── */}
      <div className="flex items-center gap-4 pt-2">
        <button
          type="button"
          data-testid="continue-btn"
          disabled={!canContinue}
          onClick={onContinue}
          className="px-5 py-2.5 bg-brand-green text-white rounded-lg text-sm font-medium hover:bg-brand-green/90 disabled:opacity-40"
        >
          Continue to step 5
        </button>
        <button
          type="button"
          data-testid="skip-btn"
          onClick={onSkip}
          className="text-sm text-brand-muted hover:text-brand-ink"
        >
          I'll come back to this
        </button>
      </div>
    </div>
  );
}
